import { getHomes } from '@services/homesService';
import { PerformanceModelsSearchResponse } from 'types/FindAHome/PerformanceModels';
import { FilterProps, LocationProps } from 'types/FindAHome/HomeSearchProps';
import { crossMod } from '@components/FindAHome/crossModJson/crossMod';
import {
  ALL_MODELS_TYPE_ID,
  MOVE_IN_READY_TYPE_ID,
  MODELS_PER_PAGE,
} from '@utils/homeSearchConstants';
import { useQuery } from '@tanstack/react-query';

type Props = FilterProps & LocationProps & { searchParamsSynced: boolean };

export function useGetHomes({
  minSquareFeet,
  maxSquareFeet,
  baths,
  beds,
  distance,
  latitude,
  longitude,
  minPrice,
  maxPrice,
  isMoveInReady,
  section,
  sortId,
  features,
  searchParamsSynced,
  city,
  state,
  postalCode,
  page,
}: Props) {
  const queryResults = useQuery({
    queryKey: [
      'home_models',
      page,
      minSquareFeet,
      maxSquareFeet,
      baths,
      beds,
      distance,
      latitude,
      longitude,
      minPrice,
      maxPrice,
      section,
      sortId,
      isMoveInReady,
      features,
    ],
    queryFn: async (): Promise<PerformanceModelsSearchResponse | undefined> => {
      if (section === 'crossmod') {
        const crossModResult = { ...crossMod };
        if (isMoveInReady) {
          crossModResult.models = []; // These homes are from a json file, so none would be home with land.
        }
        modifyImages(crossModResult);
        return crossModResult;
      } else {
        const isMultiSection = section === 'multi' ? true : null;
        const isCrossMod = null;
        const modelTypeId = isMoveInReady
          ? MOVE_IN_READY_TYPE_ID
          : ALL_MODELS_TYPE_ID;

        const result = await getHomes(
          minSquareFeet,
          maxSquareFeet,
          baths,
          beds,
          distance,
          latitude,
          longitude,
          minPrice,
          maxPrice,
          isMultiSection,
          isCrossMod,
          sortId,
          MODELS_PER_PAGE,
          page,
          modelTypeId,
          features
        );
        if (result) {
          modifyImages(result);
        }
        return result;
      }
    },
    enabled: Boolean(
      searchParamsSynced &&
        latitude &&
        longitude &&
        distance &&
        city &&
        state &&
        postalCode
    ),
    refetchOnWindowFocus: false,
    gcTime: 1000 * 60 * 15, // holds cache data for 15 minutes
    staleTime: 1000 * 60 * 15, // considers data stale after 15 minutes
    retry: 1, // retry once if first fetch attempt fails
  });

  return queryResults;
}

function modifyImages(result: PerformanceModelsSearchResponse): void {
  result.models.forEach((home) => {
    home.thumbnailImages = [
      ...(home.thumbnailImages ?? []),
      ...(home.images ?? []),
    ];
    // home.squareFeet = home.maxSquareFeet;
    const extImg = home.thumbnailImages.find((e) => e.imageType === 1);

    // Look for an exterior view in the images. If found, make it the first image displayed.
    if (extImg) {
      const idx = home.thumbnailImages.indexOf(extImg);
      //  idx is -1 if none found and 0 is already the first image.
      if (idx > 0) {
        home.thumbnailImages.splice(idx, 1);
        home.thumbnailImages.splice(0, 0, extImg);
      }
    }
  });
}
